/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import appTheme from '../../common/theme/appTheme.json';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { selectFlightLegsInfo, selectMissionPlanById, selectUserId } from '../../../store';
import { getMissionPlanById } from '../../../store/missionPlans/actions';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import MenuItems from '../../common/layout/menu/MenuItems';
import { handlePerformanceOutlookButton } from '../MissionPlans/MissionPlansTable';

export interface FlightInfoButtonsProps {}

const StyledBottom = styled(Box)`
  flex-basis: 25%;
  background-color: ${appTheme.mptPrimaryBackgroundColor};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 52px;
`;

const FlightInfoButtons = (props: FlightInfoButtonsProps) => {
  const flightLegsInfo = useSelector(selectFlightLegsInfo, shallowEqual);
  const savedMissionPlan = useSelector(selectMissionPlanById, shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  const dispatch = useDispatch();
  const history = useHistory();
  const [disabled, setDisabled] = useState<boolean>(true);

  useEffect(() => {
    if (!flightLegsInfo.length) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [flightLegsInfo]);

  // Back button press
  const handleBack = useCallback(() => {
    if (savedMissionPlan.id && userId) {
      dispatch(getMissionPlanById(userId, savedMissionPlan.id));
      history.push(`/${MenuItems.plans}/mission-details`);
    } else {
      history.push(`/${MenuItems.plans}/`);
    }
  }, [savedMissionPlan, userId, dispatch, history]);

  return (
    <StyledBottom>
      <Button
        color="primary"
        variant="outlined"
        sx={{
          fontSize: appTheme.buttonFontSize,
          borderRadius: '18px',
          fontWeight: 500,
          borderColor: appTheme.primaryTextColor,
          color: '#ffffff',
          backgroundColor: appTheme.mptPrimaryBackgroundColor,
          marginRight: '2%',
          '&:hover': {
            borderColor: appTheme.primaryTextColor,
          },
        }}
        onClick={() => handleBack()}
      >
        Back
      </Button>
      <Button
        color="primary"
        sx={{
          width: '31%',
          borderRadius: '18px',
          fontWeight: 500,
          fontSize: appTheme.buttonFontSize,
          backgroundColor: appTheme.mptLightBlue,
          color: appTheme.darkGrayTextColor,
          marginLeft: '2%',
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
          },
        }}
        onClick={() => {
          if (savedMissionPlan.id) {
            handlePerformanceOutlookButton(savedMissionPlan.id, history, dispatch);
          }
        }}
        disabled={disabled}
      >
        Generate Outlook
      </Button>
    </StyledBottom>
  );
};

export default FlightInfoButtons;
